import { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { useTestResultReducer } from "./TestResultReducer";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Textarea } from "src/sudokit/components/Textarea";
import Auth from "src/Auth";
import InlineSVG from "react-inlinesvg";
import Utils from "src/Utils";

function Tester() {
  const { values, setFieldValue } = useFormikContext();
  const [testResult, dispatchTestResult] = useTestResultReducer();
  const [isRunning, setIsRunning] = useState(false);
  const [creditCost, setCreditCost] = useState(null);
  const [isFree, setIsFree] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      const result = await Auth.fetchWithIdToken("/api/projects/list");
      const projects = result.projects.sort((a, b) => b.updatedAt._seconds - a.updatedAt._seconds);
      setProjects(projects);
      setFieldValue("testing.projectId", projects[0].id);
    };
    fetchProjects();
  }, []);

  const handleError = (error) => {
    dispatchTestResult({ type: "error", payload: error });
  };

  const handleChunk = (chunk) => {
    if (chunk?.prompt) {
      dispatchTestResult({ type: "prompt", payload: chunk });
    }
    if (chunk?.text) {
      dispatchTestResult({ type: "textChunk", payload: chunk });
    }

    if (chunk.cost) {
      setCreditCost(chunk.cost);
      if (chunk.free) {
        setIsFree(chunk.free);
      } else {
        setIsFree(false);
      }
    }
  };
  const { blocks } = values;

  const apiInputBlock = blocks.find((b) => b.blockType === "api_input");
  const apiInputVariables = apiInputBlock?.options?.variables;

  const projectId = values.testing?.projectId;
  const selectedProject = projects.find((p) => p.id === projectId);
  const documentId = selectedProject?.contents?.contents?.find((c) => c.type === "document")?.id;

  return (
    <div className="flex flex-col gap-2 mb-8">
      <h2>Test</h2>
      <h3>Projects</h3>
      <div>
        <p>Select a project to use as context for the test. </p>
        <select
          className="border rounded p-2"
          value={projectId || ""}
          onChange={(e) => setFieldValue("testing.projectId", e.target.value)}
        >
          <option value="">None</option>
          {projects.map((project) => (
            <option
              key={project.id}
              value={project.id}
            >
              {project.title}
            </option>
          ))}
        </select>
      </div>
      <h3 className="mt-4">variables</h3>
      <div>
        {apiInputVariables.map((variable) => {
          const variablePath = `testing.variables.${variable.name}`;

          return (
            <div
              key={variable.name}
              className="flex flex-col gap-2 mb-4"
            >
              <label className="font-medium">
                {variable.name}
                {variable.description ? `: ${variable.description}` : ""}
              </label>
              <Textarea
                type="text"
                className="border rounded p-2"
                placeholder={`Enter value for ${variable.name}`}
                value={values.testing?.variables?.[variable.name] || ""}
                onChange={(e) => setFieldValue(variablePath, e.target.value)}
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-2">
        <SudoButton
          variant="secondary"
          onClick={async (e) => {
            e.preventDefault();
            dispatchTestResult({ type: "reset" });
            setIsRunning(true);
            setCreditCost(null);
            const params = {
              variables: values.testing?.variables || {},
              test: true,
              definition: values,
              projectId: projectId,
              documentId: documentId,
            };

            Auth.streamWithIdToken(
              "/api/ai/testWorkflow",
              params,
              null,
              handleChunk,
              () => {
                setIsRunning(false);
              }, // onDone
              (error) => {
                setIsRunning(false);
                handleError(error);
              } // onError
            );
          }}
          type="test"
          disabled={isRunning}
        >
          {isRunning ? "running" : "Run Test"}
        </SudoButton>
      </div>
      <div className="mt-4">
        <h3>Test Result: </h3>
        {testResult.map((responseBlock, i) => {
          if (responseBlock.type === "prompt") {
            return (
              <div
                key={`response-${i}`}
                className="mt-4"
              >
                <h4>{responseBlock.title}</h4>
                {responseBlock.content.map((message, j) => (
                  <div key={`message-${j}`}>{Utils.nl2pRaw(message.content)}</div>
                ))}
              </div>
            );
          } else if (responseBlock.type === "error") {
            return (
              <div
                key={`response-${i}`}
                className="mt-4 text-red-500"
              >
                <h4>Error</h4>
                <p>{responseBlock.content}</p>
                <p>If you are using story bible, make sure you have a story bible project selected.</p>
              </div>
            );
          } else {
            return (
              <div
                key={`response-${i}`}
                className="mt-4"
              >
                <h4>{responseBlock.isForEndUser ? "Card Output" : responseBlock.title}</h4>
                {Utils.nl2pRaw(responseBlock.content)}
              </div>
            );
          }
        })}
        {creditCost && (
          <div className="flex flex-row items-center mt-4 gap-2">
            <h2 className="text-lg font-bold mb-0">Credits used: </h2>
            <InlineSVG
              aria-label="credits"
              src="/assets/pricing/credits.svg"
            />
            <span>{creditCost}</span>
            {isFree && <strong className="text-green-500">Free</strong>}
          </div>
        )}
      </div>
    </div>
  );
}

export default Tester;
